import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from '@/components/feature/AppLayout';
import { activityRecords } from '@/mocks/systemData';

export default function SupEscalationPage() {
  const navigate = useNavigate();
  const [escalatedCases, setEscalatedCases] = useState<any[]>([]);
  const [workers, setWorkers] = useState<any[]>([]);
  const [reassignModal, setReassignModal] = useState<string | null>(null);
  const [reassignWorker, setReassignWorker] = useState('');
  const [delayedCases, setDelayedCases] = useState<any[]>([]);

  const getRisk = (c: any) => {
    let score = 75;
    switch (c.incident_type) {
      case 'Sexual Abuse':
      case 'Child Trafficking':
      case 'Online Sexual Exploitation':
        score = 95;
        break;
      case 'Physical Abuse':
        score = 90;
        break;
      case 'Emotional Abuse':
        score = 80;
        break;
      case 'Neglect':
      case 'Child Labor':
      case 'Abandonment':
        score = 75;
        break;
      default:
        score = 70;
    }
    if (c.prior_incidents === 'yes')
      score += 5;
    if (c.priority_level === 'urgent')
      score += 3;
    if (c.priority_level === 'emergency')
      score += 5;
    if (score > 100)
      score = 100;
    let level = 'low';
    if (score >= 90)
      level = 'critical';
    else if (score >= 80)
      level = 'high';
    else if (score >= 75)
      level = 'moderate';
    return {
      score,
      level
    };
  };

  useEffect(() => {

    fetch(
      'https://guardiancloudcare.com/backend/api/get_escalated_cases.php'
    )
    .then(res => res.json())
    .then(data => {
      if (Array.isArray(data)) {
        setEscalatedCases(data);
      }
    });

    fetch(
      'https://guardiancloudcare.com/backend/api/get_social_workers.php'
    )
    .then(res => res.json())
    .then(data => {
      if (Array.isArray(data)) {
        setWorkers(data);
      }
    });

    fetch(
      'https://guardiancloudcare.com/backend/api/get_delayed_cases.php'
    )
    .then(res => res.json())
    .then(data => {

      if (Array.isArray(data)) {

        setDelayedCases(data);

      }

    });

  }, []);

  const popupStyle = `
  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
  `;

  return (
    <AppLayout
      title="Escalation & Accountability"
      breadcrumb={['Supervisor', 'Escalation']}
    >
      <div className="space-y-4">
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
          <i className="ri-alarm-warning-line text-red-500 text-xl" />
          <div>
            <p className="text-sm font-semibold text-red-800">
              Escalated & Delayed Cases
            </p>
            <p className="text-xs text-red-600">
              {escalatedCases.length} critical cases +{' '}
              {delayedCases.length}
              {' '}delayed cases need attention
            </p>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-amber-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-amber-100 bg-amber-50 flex items-center gap-2">
            <i className="ri-time-line text-amber-600" />
            <h3 className="font-semibold text-amber-800">
              Inactive Social Workers / Delayed Cases
            </h3>
          </div>
          <div className="divide-y divide-gray-50">
            {delayedCases.map((a, index) => (
              <div
                key={index}
                className="px-5 py-4 flex items-center gap-4"
              >
                <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-time-line text-amber-600" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-800">
                    {a.worker_name} — {a.action_type}
                  </p>
                  <p className="text-xs font-mono text-gray-400">
                    {a.case_number} · {a.activity_date}
                  </p>
                </div>
                {a.delayDays && (
                  <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full font-medium">
                    {a.delay_days} days delayed
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
        {escalatedCases.map((c) => {
          const risk = getRisk(c);
          return (
            <div
              key={c.id}
              className="bg-white rounded-xl border border-red-200 overflow-hidden"
            >
              <div className="px-5 py-4 bg-red-50 border-b border-red-100 flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-gray-900">
                    {c.first_name} {c.last_name}
                  </p>
                  <p className="text-xs font-mono text-gray-500">
                    {c.case_number}
                  </p>
                </div>
                <span
                  className={`text-xs px-2 py-1 rounded-full font-medium capitalize ${
                    risk.level === 'critical'
                      ? 'bg-red-100 text-red-700'
                      : risk.level === 'high'
                      ? 'bg-orange-100 text-orange-700'
                      : risk.level === 'moderate'
                      ? 'bg-amber-100 text-amber-700'
                      : 'bg-green-100 text-green-700'
                  }`}
                >
                  {risk.level} ({risk.score})
                </span>
              </div>
              <div className="px-5 py-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4 text-sm">
                  <div>
                    <p className="text-xs text-gray-400">
                      Incident
                    </p>
                    <p className="font-medium text-gray-800">
                      {c.incident_type || 'No Incident'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">
                      Location
                    </p>
                    <p className="font-medium text-gray-800">
                      {c.barangay}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">
                      Assigned To
                    </p>
                    <p className="font-medium text-gray-800">
                      {c.worker_name || 'Unassigned'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">
                      Created At
                    </p>
                    <p className="font-medium text-gray-800">
                      {c.created_at || 'No Date'}
                    </p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() =>
                      navigate(`/child-profiles/${c.case_number}`)
                    }
                    className="text-sm border border-gray-200 text-gray-700 px-4 py-2 rounded-xl hover:bg-gray-50 cursor-pointer whitespace-nowrap flex items-center gap-1"
                  >
                    <i className="ri-eye-line" />
                    View Details
                  </button>
                  <button
                    onClick={() => setReassignModal(c.id)}
                    className="text-sm bg-amber-500 text-white px-4 py-2 rounded-xl hover:bg-amber-600 cursor-pointer whitespace-nowrap flex items-center gap-1"
                  >
                    <i className="ri-user-shared-line" />
                    Reassign
                  </button>
                  <button
                    onClick={async () => {

                      await fetch(
                        'https://guardiancloudcare.com/backend/api/mark_priority.php',
                        {
                          method: 'POST',
                          headers: {
                            'Content-Type': 'application/json'
                          },
                          body: JSON.stringify({

                            case_number:
                              c.case_number,

                            priority:
                              c.mark_as_priority == 1
                                ? 0
                                : 1

                          })
                        }
                      );

                      window.location.reload();

                    }}

                    className={`text-sm px-4 py-2 rounded-xl cursor-pointer whitespace-nowrap flex items-center gap-1 ${
                      c.mark_as_priority == 1
                        ? 'bg-green-500 text-white hover:bg-green-600'
                        : 'bg-red-500 text-white hover:bg-red-600'
                    }`}
                  >

                    <i className={
                      c.mark_as_priority == 1
                        ? 'ri-checkbox-circle-line'
                        : 'ri-alarm-warning-line'
                    } />

                    {c.mark_as_priority == 1
                      ? 'Prioritized'
                      : 'Mark Priority'}

                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {reassignModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 w-full max-w-md">
            <h3 className="text-lg font-bold text-gray-900 mb-4">
              Reassign Case
            </h3>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1.5 block">
                Assign to Social Worker
              </label>
              <select
                value={reassignWorker}
                onChange={(e) =>
                  setReassignWorker(e.target.value)
                }
                className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm outline-none focus:border-teal-500 cursor-pointer"
              >
                <option value="">
                  Select social worker...
                </option>
                {workers.map((w) => (
                  <option
                    key={w.uid}
                    value={w.uid}
                  >

                    {w.name}

                  </option>
                ))}
              </select>
            </div>
            <div className="flex gap-3 justify-end mt-5">
              <button
                onClick={() => setReassignModal(null)}
                className="text-sm border border-gray-200 px-4 py-2 rounded-xl cursor-pointer whitespace-nowrap hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={async () => {

                  const selectedCase =
                    escalatedCases.find(
                      c => c.id === reassignModal
                    );

                  await fetch(
                    'https://guardiancloudcare.com/backend/api/reassign_case.php',
                    {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json'
                      },
                      body: JSON.stringify({

                        case_number:
                          selectedCase.case_number,

                        uid:
                          reassignWorker

                      })
                    }
                  );

                  setReassignModal(null);

                  const popup = document.createElement('div');

                  popup.innerHTML = `
                    <div style="
                      position: fixed;
                      top: 30px;
                      right: 30px;
                      background: #14b8a6;
                      color: white;
                      padding: 16px 24px;
                      border-radius: 14px;
                      font-size: 14px;
                      font-weight: 600;
                      z-index: 99999;
                      box-shadow: 0 10px 25px rgba(0,0,0,0.15);
                      display: flex;
                      align-items: center;
                      gap: 10px;
                      animation: slideIn 0.3s ease;
                    ">
                      <i class="ri-checkbox-circle-fill"></i>
                      Case reassigned successfully
                    </div>
                  `;

                  document.body.appendChild(popup);

                  setTimeout(() => {

                    popup.remove();

                    window.location.reload();

                  }, 2000);

                  window.location.reload();

                }}
                className="text-sm bg-teal-500 text-white px-5 py-2 rounded-xl hover:bg-teal-600 cursor-pointer whitespace-nowrap"
              >
                Reassign
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}